import { auth } from '@/state/auth'
import axios from 'axios'
import type { Project } from 'functions/_db/schema'
import { produce } from 'immer'
import { create } from 'zustand'

type ProjectsState = {
  empty: boolean
  loading: boolean
  list: Project[]
  fetch(): Promise<void>
  reset(): void
  create(title: string, description: string): Promise<Project>
  update(id: number, title: string, description: string): Promise<Project>
  remove(id: number): Promise<void>
}

export const useProjectsState = create<ProjectsState>(set => ({
  empty: false,
  loading: false,
  list: [],

  fetch: async () => {
    set({ loading: true })
    try {
      const { data } = await auth(token => (
        axios.get<Project[]>('/api/projects', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
      ))
      set({ list: data, empty: data.length === 0 })
    }
    catch (e) {
      console.error(e)
      set({ list: [] })
    }
    finally {
      set({ loading: false })
    }
  },

  create: async (title, description) => {
    set({ loading: true })
    try {
      const { data } = await auth(token => (
        axios.post<Project>('/api/projects', {
          title,
          description,
        }, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
      ))

      set(produce((s: ProjectsState) => {
        s.list.push(data)
      }))

      return data
    }
    catch (e) {
      console.error(e)
      throw e
    }
    finally {
      set({ loading: false })
    }
  },

  update: async (id, title, description) => {
    set({ loading: true })
    try {
      const { data } = await auth(token => (
        axios.put<Project>(
          `/api/projects/${id}`,
          { title, description },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )
      ))

      set(produce((s: ProjectsState) => {
        const index = s.list.findIndex(p => p.id === id)
        if (index !== -1) {
          s.list[index] = data
        }
      }))

      return data
    }
    catch (e) {
      console.error(e)
      throw e
    }
    finally {
      set({ loading: false })
    }
  },

  remove: async id => {
    try {
      await auth(token => (
        axios.delete(`/api/projects/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
      ))
      set(produce((s: ProjectsState) => {
        s.list = s.list.filter(p => p.id !== id)
        s.empty = s.list.length === 0
      }))
    }
    catch (e) {
      console.error(e)
    }
  },

  reset: () => {
    set({
      loading: false,
      empty: false,
      list: [],
    })
  },
}))
